import { type ApprovalProcess, type ApprovalScopeEnum, type Employee, type Expense } from "@finway-group/shared/lib/models"
import { getApprovalProcess } from "@finway-group/shared/lib/utils"
import { useState } from "react"
import { useRolesMap } from "~Shared/hooks/role.hooks"
import { EmployeeService } from "~Shared/services"
import { getAbsentApprovers, getApproversBlockingOnAbsence, getAvailableReplacementsForAbsentApprover } from "../utils/employee.utils"

interface UseReplacementForAbsentApproverParams {
    employees: Array<Employee> | undefined
    checkSubstitutedBy?: boolean
}

interface CheckAbsentApproversParams {
    entity: Pick<Expense, "requestedBy"> & Partial<Pick<Expense, "paymentOption">> & { approvalProcesses?: Array<ApprovalProcess> }
    scope: ApprovalScopeEnum
}

/**
 * Custom hook to manage the replacement of absent approvers workflow.
 * This hook handles the detection of absent approvers and the selection of replacements.
 */
export const useReplacementForAbsentApprover = ({ employees, checkSubstitutedBy = true }: UseReplacementForAbsentApproverParams) => {
    const [isReplacementForAbsentApproverShowing, setIsReplacementForAbsentApproverShowing] = useState(false)
    const [absentApprovers, setAbsentApprovers] = useState<Array<Employee>>([])
    const [availableReplacementsForAbsentApprover, setAvailableReplacementsForAbsentApprover] = useState<Array<Employee>>([])
    const [checkIfApproverInAbsence, setCheckIfApproverInAbsence] = useState(true)
    const [replacementForAbsentApprover, setReplacementForAbsentApprover] = useState<string | undefined>(undefined)

    const rolesMap = useRolesMap({ excludeDeleted: true })

    /**
     * Shows the replacement modal when absence stops the approval process.
     * An OR step counts only when every approver is absent and none of them has a substitute.
     * An AND step counts when any approver is.
     *
     * @param entity - The entity to check (e.g., expense, purchase order)
     * @param scope - The approval scope to check (e.g., EXPENSE_PURCHASE, PURCHASE_ORDER)
     * @returns true if the modal was shown, false otherwise
     */
    const checkForAbsentApprovers = async ({ entity, scope }: CheckAbsentApproversParams): Promise<boolean> => {
        if (!checkIfApproverInAbsence || replacementForAbsentApprover || !entity?.approvalProcesses || !employees) {
            return false
        }

        const approvalProcess = getApprovalProcess(scope, entity.approvalProcesses)
        if (!approvalProcess) {
            return false
        }

        const absentApproversList = getAbsentApprovers(approvalProcess, employees)
        if (absentApproversList.length === 0) {
            return false
        }

        // substitutedBy is only returned by /employees/:id
        const unavailableApprovers = checkSubstitutedBy
            ? (await Promise.all(absentApproversList.map((approver) => EmployeeService.fetchEmployeeById(approver.id)))).filter(
                  (approver): approver is Employee => approver !== null && !approver.substitutedBy,
              )
            : absentApproversList
        if (unavailableApprovers.length === 0) {
            return false
        }

        const blockingApprovers = getApproversBlockingOnAbsence(approvalProcess, employees, new Set(unavailableApprovers.map((approver) => approver.id)))
        if (blockingApprovers.length === 0) {
            return false
        }

        setAvailableReplacementsForAbsentApprover(getAvailableReplacementsForAbsentApprover(entity, employees, rolesMap))
        setAbsentApprovers(blockingApprovers)
        setIsReplacementForAbsentApproverShowing(true)

        return true
    }

    /**
     * Closes the replacement modal and optionally resets the replacement selection.
     */
    const closeModal = () => {
        setIsReplacementForAbsentApproverShowing(false)
    }

    return {
        // State
        isReplacementForAbsentApproverShowing,
        absentApprovers,
        availableReplacementsForAbsentApprover,
        replacementForAbsentApprover,

        // Functions
        checkForAbsentApprovers,
        setCheckIfApproverInAbsence,
        setReplacementForAbsentApprover,
        closeModal,
    }
}
